import { WALLET_TYPES, BlockchainNetwork } from '~/types'

export type Wallet = {
  type: WALLET_TYPES
  balance: string
  label: string
  accountType: string
  accountId: number
}

export type Account = {
  attributes: {
    wallet_id: number
    limitations: {}
    type: string
  }
}

export type BankAccount = {
  bankName: string
  accountNumber: string
  id: number
  status: 'unverified' | 'verified' | 'pending' | 'rejected'
}

export type BlockchainAddress = {
  id: number
  blockchain: string
  address: string
}

export type AccountLimits = {
  holdingRemaining: string
  holdingMax: string
}

export type TransferInfo = {
  recipientName: string
  recipientBankName: string
  recipientAccountNo: string
  referenceNumber?: string
  expiredDate?: string
  depositAmount: string
  userBankAccount: string
  userHandPhoneNo?: string
}

export type CopyTextCountry = {
  id: CopyTextViewType
  sg: CopyTextViewType
}

export type CopyTextViewType = {
  walletForm: WalletFormCopyText
}

export type WalletFormCopyText = {
  title: string
  subtitle: string
  selectTitle: string
  placeholder: string
}

export enum DEPOSIT_ACTIONS {
  SET_IS_LOADING = 'DEPOSIT_ACTIONS.SET_IS_LOADING',
  SET_IS_ADDRESS_LOADING = 'DEPOSIT_ACTIONS.SET_IS_ADDRESS_LOADING',
  SET_BANK_ACCOUNTS = 'DEPOSIT_ACTIONS.SET_BANK_ACCOUNTS',
  SET_VERIFICATION_STATUS = 'DEPOSIT_ACTIONS.SET_VERIFICATION_STATUS',
  SET_DESTINATION = 'DEPOSIT_ACTIONS.SET_DESTINATION',
  SET_DESTINATION_AMOUNT = 'DEPOSIT_ACTIONS.SET_DESTINATION_AMOUNT',
  SET_BLOCKCHAIN_ADDRESSES = 'DEPOSIT_ACTIONS.SET_BLOCKCHAIN_ADDRESSES',
  SET_SELECTED_BLOCKCHAIN_NEWTWORK = 'DEPOSIT_ACTIONS.SET_SELECTED_BLOCKCHAIN_NETWORK',
  GET_ACCOUNT_LIMITS = 'DEPOSIT_ACTIONS.GET_ACCOUNT_LIMITS',
  CREATE_TOPUP_REQUEST = 'DEPOSIT_ACTIONS.CREATE_TOPUP_REQUEST',
  SET_TRANSFER_INFO = 'DEPOSIT_ACTIONS.SET_TRANSFER_INFO',
  CREATE_BLOCKCHAIN_ADDRESS = 'DEPOSIT_ACTIONS.CREATE_BLOCKCHAIN_ADDRESS',
  SET_UNIQUE_AMT_PAYMENT_INSTRUCTIONS = 'SET_UNIQUE_AMT_PAYMENT_INSTRUCTIONS',
  SET_ACCOUNTS = 'DEPOSIT_ACTIONS.SET_ACCOUNTS',
  SET_IS_ERC20_ENABLED = 'DEPOSIT_ACTIONS.SET_IS_ERC20_ENABLED',
  SET_IS_ZRC2_ENABLED = 'DEPOSIT_ACTIONS.SET_IS_ZRC2_ENABLED',
  SET_IS_ZRC2_NOTICE_BANNER_VISIBLE = 'DEPOSIT_ACTIONS.SET_IS_ZRC2_NOTICE_BANNER_VISIBLE',
  SET_IS_ERC20_DEPOSIT_FEE_CHARGING = 'DEPOSIT_ACTIONS.SET_IS_ERC20_DEPOSIT_FEE_CHARGING',
  SET_IS_ZRC2_DEPOSIT_FEE_CHARGING = 'DEPOSIT_ACTIONS.SET_IS_ZRC2_DEPOSIT_FEE_CHARGING',
  SET_IS_VA_ENABLED = 'DEPOSIT_ACTIONS.SET_IS_VA_ENABLED',
  SET_IS_STRAITSX_USER_DEPOSIT_FLOW_IN_ONE_STEP = 'DEPOSIT_ACTIONS.SET_IS_STRAITSX_USER_DEPOSIT_FLOW_IN_ONE_STEP',
}

type SetIsLoading = {
  type: typeof DEPOSIT_ACTIONS.SET_IS_LOADING
  isLoading: boolean
}

type SetIsAddressLoading = {
  type: typeof DEPOSIT_ACTIONS.SET_IS_ADDRESS_LOADING
  isAddressLoading: boolean
}

type SetBankAccounts = {
  type: typeof DEPOSIT_ACTIONS.SET_BANK_ACCOUNTS
  bankAccounts: BankAccount[]
}

type SetVerificationStatus = {
  type: typeof DEPOSIT_ACTIONS.SET_VERIFICATION_STATUS
  personalVerificationStatus: string
}

type SetDestination = {
  type: typeof DEPOSIT_ACTIONS.SET_DESTINATION
  destination: number | undefined
}

type SetDestinationAmount = {
  type: typeof DEPOSIT_ACTIONS.SET_DESTINATION_AMOUNT
  destinationAmount: string
}

type SetBlockchainAddresses = {
  type: typeof DEPOSIT_ACTIONS.SET_BLOCKCHAIN_ADDRESSES
  blockchainAddresses: BlockchainAddress[]
}

type SetSelectedBlockchainNetwork = {
  type: typeof DEPOSIT_ACTIONS.SET_SELECTED_BLOCKCHAIN_NEWTWORK
  selectedBlockchainNetwork: BlockchainNetwork | null
}

type GetAccountLimits = {
  type: typeof DEPOSIT_ACTIONS.GET_ACCOUNT_LIMITS
  accountLimits: AccountLimits
}

type CreateTopupRequest = {
  type: typeof DEPOSIT_ACTIONS.CREATE_TOPUP_REQUEST
  transferInfo: TransferInfo
}

type SetTransferInfo = {
  type: typeof DEPOSIT_ACTIONS.SET_TRANSFER_INFO
  transferInfo: TransferInfo
}

type CreateBlockchainAddress = {
  type: typeof DEPOSIT_ACTIONS.CREATE_BLOCKCHAIN_ADDRESS
  blockchainAddress: BlockchainAddress
}

type SetAccounts = {
  type: typeof DEPOSIT_ACTIONS.SET_ACCOUNTS
  accounts: { [key: string]: Account }
}

type SetIsErc20Enabled = {
  type: typeof DEPOSIT_ACTIONS.SET_IS_ERC20_ENABLED
  isErc20Enabled: boolean
}

type SetIsZrc2Enabled = {
  type: typeof DEPOSIT_ACTIONS.SET_IS_ZRC2_ENABLED
  isZrc2Enabled: boolean
}

type SetIsZrc2NoticeBannerEnabled = {
  type: typeof DEPOSIT_ACTIONS.SET_IS_ZRC2_NOTICE_BANNER_VISIBLE
  isZrc2NoticeBannerVisible: boolean
}

type SetIsErc20DepositFeeCharging = {
  type: typeof DEPOSIT_ACTIONS.SET_IS_ERC20_DEPOSIT_FEE_CHARGING
  isErc20DepositFeeCharging: boolean
}

type SetIsZrc2DepositFeeCharging = {
  type: typeof DEPOSIT_ACTIONS.SET_IS_ZRC2_DEPOSIT_FEE_CHARGING
  isZrc2DepositFeeCharging: boolean
}

type SetIsVAEnabled = {
  type: typeof DEPOSIT_ACTIONS.SET_IS_VA_ENABLED
  isVAEnabled: boolean
}

type SetIsStraitsxUserDepositFlowInOneStep = {
  type: typeof DEPOSIT_ACTIONS.SET_IS_STRAITSX_USER_DEPOSIT_FLOW_IN_ONE_STEP
  isStraitsxUserDepositFlowInOneStep: boolean
}

export type ActionTypes =
  | SetIsLoading
  | SetIsAddressLoading
  | SetBankAccounts
  | SetVerificationStatus
  | SetDestination
  | SetDestinationAmount
  | SetBlockchainAddresses
  | SetSelectedBlockchainNetwork
  | GetAccountLimits
  | CreateTopupRequest
  | SetTransferInfo
  | CreateBlockchainAddress
  | SetAccounts
  | SetIsErc20Enabled
  | SetIsZrc2Enabled
  | SetIsZrc2NoticeBannerEnabled
  | SetIsErc20DepositFeeCharging
  | SetIsZrc2DepositFeeCharging
  | SetIsVAEnabled
  | SetIsStraitsxUserDepositFlowInOneStep
